import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, type PlatformSkill, type PlatformSkills } from '../api/client'
import { useFleetVersion } from '../hooks/useFleetVersion'
import { agentCountText, appliesToText, platformSkillPath } from './platformSkills'

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export function PlatformSkillsArea({ selectedName }: { selectedName?: string }) {
  const navigate = useNavigate()
  const fleetSha = useFleetVersion()
  const [data, setData] = useState<PlatformSkills | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const requestSequence = useRef(0)
  const skipFirstSha = useRef(true)
  const dataRef = useRef<PlatformSkills | null>(null)

  const fetchSkills = useCallback(async (refresh: boolean) => {
    const sequence = ++requestSequence.current
    if (!refresh) setLoading(true)
    try {
      const result = await api.listPlatformSkills()
      if (sequence !== requestSequence.current) return
      dataRef.current = result
      setData(result)
      setLoadError(null)
      setRefreshError(null)
      setLoading(false)
    } catch (error) {
      if (sequence !== requestSequence.current) return
      if (dataRef.current === null) setLoadError(errorMessage(error))
      else setRefreshError(`Could not refresh platform skills: ${errorMessage(error)}`)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const sequenceRef = requestSequence
    void fetchSkills(false)
    return () => {
      sequenceRef.current++
    }
  }, [fetchSkills])

  useEffect(() => {
    if (!fleetSha) return
    if (skipFirstSha.current) {
      skipFirstSha.current = false
      return
    }
    void fetchSkills(true)
  }, [fleetSha, fetchSkills])

  const skills = useMemo(() => data?.skills ?? [], [data?.skills])
  useEffect(() => {
    if (loading || selectedName || skills.length === 0) return
    navigate(platformSkillPath(skills[0].name), { replace: true })
  }, [loading, selectedName, skills, navigate])

  const selected = selectedName ? skills.find(skill => skill.name === selectedName) : undefined

  function selectSkill(skill: PlatformSkill) {
    navigate(platformSkillPath(skill.name), { replace: true })
  }

  if (loading && data === null) {
    return <p className="p-6 text-sm text-gray-500">Loading platform skills…</p>
  }
  if (loadError && data === null) {
    return <div className="m-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{loadError}</div>
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-auto p-4">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900">Platform skills</h1>
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">core-owned · read-only</span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Universal skills from agentcore, composed onto agents at deploy. This fleet&apos;s repo does not carry them, so they are not edited here. Changes go through the core propose lane.
        </p>
      </header>

      {refreshError && <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{refreshError}</div>}

      {skills.length === 0 ? (
        <p className="rounded border border-gray-200 p-4 text-sm text-gray-500">The platform manifest binds no universal skills.</p>
      ) : (
        <div className="grid min-h-0 shrink-0 items-start gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
          <div className="overflow-auto rounded border border-gray-200">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr><th className="px-3 py-2">Skill</th><th className="px-3 py-2">Version</th><th className="px-3 py-2">Applies to</th><th className="px-3 py-2">Agents</th></tr>
              </thead>
              <tbody>
                {skills.map((skill, index) => (
                  <tr key={skill.name} className={selectedName === skill.name ? 'bg-indigo-50' : 'border-t border-gray-100'}>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => selectSkill(skill)} className="text-left font-medium text-indigo-700 hover:underline">
                        {skill.name}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-600">{skill.version || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{appliesToText(skill.exclude_roles)}</td>
                    {data?.roster_available ? (
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">{agentCountText(skill.composed_count, data.roster_size, true)}</td>
                    ) : index === 0 ? (
                      <td rowSpan={skills.length} className="px-3 py-2 align-middle text-gray-600">{agentCountText(0, 0, false)}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <section className="min-w-0 rounded border border-gray-200 p-4">
            {!selectedName ? (
              <p className="text-sm text-gray-500">Select a platform skill to see its details.</p>
            ) : !selected ? (
              <p className="text-sm text-gray-500">No platform skill named &quot;{selectedName}&quot;.</p>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900">{selected.name}</h2>
                <p className="mt-1 break-all font-mono text-xs text-gray-500">{selected.path}</p>
                {!selected.file_present && <p className="mt-2 text-xs text-amber-700">This Mission Control image does not carry the skill file.</p>}
                <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{selected.description}</p>
                <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide text-gray-500">Composed onto</h3>
                {!data?.roster_available ? (
                  <p className="mt-2 text-sm text-gray-500">The fleet roster could not be read, so which agents get this skill is unknown.</p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.agents.map(agent => {
                      const excluded = !agent.composed
                      return (
                        <span key={agent.agent} className={`rounded-full border px-2 py-1 text-xs ${excluded ? 'border-dashed border-gray-400 text-gray-500 line-through' : 'border-gray-200 text-gray-700'}`}>
                          {excluded ? `${agent.agent} (${agent.role} role excluded)` : agent.agent}
                        </span>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      <footer className="border-t border-gray-100 pt-3 text-xs text-gray-500">
        Role-bound core skills (the brain&apos;s delegation-protocol, the orchestrator&apos;s escalation-routing and others) stay listed under their agent, read-only.
      </footer>
    </div>
  )
}
